import {
  freeAccessOf, isFreeAccessActive, mergeFreeAccess, FREE_ACCESS_REMINDER_WINDOW_DAYS,
} from '@/libs/freeAccess'

const REVIEW_STATES = new Set(['not-started', 'active', 'completed', 'not-applicable'])

const booleanOr = (value, fallback = false) => (
  typeof value === 'boolean' ? value : fallback
)

const approvalStatusOf = account => {
  const latestStatus = account && account.approval && account.approval.status
  const standing = account && account.approvalStanding
  // /subscription/status is fetched after /user/my and already projects the
  // newest account-approval row for sticky-approved accounts. Always prefer
  // that non-null projection so an admin decision committed between the two
  // requests cannot be overwritten by the older /user/my response.
  if (standing && standing.approvalStatus) return standing.approvalStatus
  if (latestStatus) return latestStatus
  if (account && account.awaitingApproval) return 'pending'
  if (account && account.rejectedApproval) return 'rejected'
  if (account && account.approved) return 'approved'
  return 'not-submitted'
}

const accessOf = account => {
  if (account && account.subscription && typeof account.subscription.hasAccess === 'boolean') {
    return account.subscription.hasAccess
  }
  return account && typeof account.hasAccess === 'boolean' ? account.hasAccess : false
}

const reviewOf = account => {
  const review = account && account.approvalReview
  if (!review || !REVIEW_STATES.has(review.state)) {
    return { state: 'unknown', startedAt: null, deadlineAt: null }
  }
  return {
    state: review.state,
    startedAt: review.startedAt || null,
    deadlineAt: review.deadlineAt || null,
  }
}

const standingOf = account => {
  const standing = account && account.approvalStanding
  return standing && typeof standing.allowed === 'boolean' ? standing : null
}

export const mergeApprovalLifecycleAccount = (account = {}, standing = null) => {
  const source = standing || {}
  const existingSubscription = account.subscription || {}
  const statusHasAccess = typeof source.hasAccess === 'boolean'
    ? source.hasAccess
    : existingSubscription.hasAccess
  const statusApproved = typeof source.approved === 'boolean' ? source.approved : account.approved
  const statusBlocked = typeof source.accountBlocked === 'boolean'
    ? source.accountBlocked
    : account.accountBlocked

  return {
    ...account,
    approved: booleanOr(statusApproved),
    accountBlocked: booleanOr(statusBlocked),
    approvalStanding: source.approvalStanding || account.approvalStanding || null,
    approvalReview: source.approvalReview || account.approvalReview || null,
    freeAccess: mergeFreeAccess(source.freeAccess, account.freeAccess, existingSubscription.freeAccess),
    promotion: source.promotion || account.promotion || null,
    canUseGatedFeatures: typeof source.canUseGatedFeatures === 'boolean'
      ? source.canUseGatedFeatures
      : account.canUseGatedFeatures,
    subscription: {
      ...existingSubscription,
      status: source.subscriptionStatus || existingSubscription.status || 'INACTIVE',
      hasSubscription: typeof source.hasSubscription === 'boolean'
        ? source.hasSubscription
        : existingSubscription.hasSubscription,
      hasAccess: booleanOr(statusHasAccess),
      accessReason: source.accessReason || existingSubscription.accessReason || null,
      trialEndsAt: source.trialEndsAt || existingSubscription.trialEndsAt || null,
      // The retained-access boundary written at cancel time. The status
      // endpoint has always exposed it as `offer.blockedUntil` (the same
      // date services/subscriptionBoundary.js computes), but this merge used
      // to drop `offer` entirely -- so the dashboard could never tell that a
      // subscription had been canceled, let alone until when.
      accessEndsAt: (source.offer && source.offer.blockedUntil)
        || existingSubscription.accessEndsAt
        || null,
      // PM 14.08.2026 item 3: /subscription/status has always sent
      // subscriptionStartedAt (controllers/subscription/status.js) and
      // subscriptionStanding normalizes it; this merge just never forwarded
      // it. It is the identity of the current subscription term, which is what
      // keys the one-time welcome.
      startedAt: source.subscriptionStartedAt
        || existingSubscription.startedAt
        || null,
    },
  }
}

// Only a CANCELED subscription still inside its retained-access window gets
// the cancellation notice. Once the boundary has passed, telling the user
// they may use the system until a date already gone would be false, so they
// fall through to the normal states.
const retainedCancellationBoundary = (subscription, now = Date.now()) => {
  if (!subscription || subscription.status !== 'CANCELED') return null
  const raw = subscription.accessEndsAt
  if (!raw) return null
  const boundary = new Date(raw)
  if (Number.isNaN(boundary.getTime())) return null
  return boundary.getTime() > now ? raw : null
}

const presentation = {
  blocked: {
    titleKey: 'approvalLifecycle.blocked.title',
    bodyKeys: ['approvalLifecycle.blocked.body'],
    variant: 'danger',
    icon: 'HelpCircleIcon',
  },
  // PM 05.08.2026 item 8: the card is a prompt to go and fill the form in, not
  // a description of what happens after submitting -- that message is already
  // shown once the information has actually been filled in. Its button
  // therefore says "complete", not "submit for approval"; the real submit
  // button on account settings keeps `notSubmitted.cta`.
  'not-submitted': {
    titleKey: 'approvalLifecycle.notSubmitted.title',
    bodyKeys: ['approvalLifecycle.notSubmitted.body'],
    variant: 'primary',
    icon: 'ClipboardIcon',
    action: { key: 'approvalLifecycle.notSubmitted.completeCta', route: 'account-settings' },
  },
  // PM 05.08.2026 item 3: the banner is the title plus one sentence. The
  // review-window timing line and the notification-channel line are both gone
  // -- the new body states the 15-day limit and both channels itself.
  submitted: {
    titleKey: 'approvalLifecycle.submitted.title',
    bodyKeys: ['approvalLifecycle.submitted.body'],
    variant: 'info',
    icon: 'CheckCircleIcon',
  },
  resubmitted: {
    titleKey: 'approvalLifecycle.submitted.title',
    bodyKeys: ['approvalLifecycle.rejected.resubmitted'],
    deadlineKey: 'approvalLifecycle.pending.active.deadline',
    showChannel: true,
    variant: 'info',
    icon: 'CheckCircleIcon',
  },
  'pending-not-started': {
    titleKey: 'approvalLifecycle.pending.title',
    bodyKeys: [
      'approvalLifecycle.pending.notStarted.body',
    ],
    showChannel: true,
    variant: 'info',
    icon: 'ClockIcon',
  },
  'pending-active': {
    titleKey: 'approvalLifecycle.pending.title',
    bodyKeys: ['approvalLifecycle.pending.active.access'],
    deadlineKey: 'approvalLifecycle.pending.active.deadline',
    showChannel: true,
    variant: 'info',
    icon: 'ClockIcon',
  },
  'pending-started-no-access': {
    titleKey: 'approvalLifecycle.pending.title',
    bodyKeys: ['approvalLifecycle.pending.reviewBody'],
    deadlineKey: 'approvalLifecycle.pending.active.deadline',
    showChannel: true,
    variant: 'info',
    icon: 'ClockIcon',
    action: { key: 'approvalLifecycle.approved.subscriptionCta', route: 'financial-settings' },
  },
  'pending-unknown': {
    titleKey: 'approvalLifecycle.pending.title',
    bodyKeys: ['approvalLifecycle.pending.reviewBody'],
    showChannel: true,
    variant: 'info',
    icon: 'ClockIcon',
  },
  'rejected-not-started': {
    titleKey: 'approvalLifecycle.rejected.title',
    bodyKeys: [
      'approvalLifecycle.rejected.body',
    ],
    promptKey: 'approvalLifecycle.rejected.resubmitPrompt',
    showReason: true,
    showPromotion: true,
    variant: 'warning',
    icon: 'Edit3Icon',
    action: { key: 'approvalLifecycle.rejected.cta', route: 'account-settings' },
  },
  'rejected-active': {
    titleKey: 'approvalLifecycle.rejected.title',
    bodyKeys: ['approvalLifecycle.rejected.body'],
    promptKey: 'approvalLifecycle.rejected.resubmitPrompt',
    deadlineKey: 'approvalLifecycle.rejected.activeCorrection',
    showReason: true,
    showPromotion: true,
    variant: 'warning',
    icon: 'Edit3Icon',
    action: { key: 'approvalLifecycle.rejected.cta', route: 'account-settings' },
  },
  'rejected-completed': {
    titleKey: 'approvalLifecycle.rejected.title',
    bodyKeys: [
      'approvalLifecycle.rejected.body',
      'approvalLifecycle.rejected.completed',
    ],
    promptKey: 'approvalLifecycle.rejected.resubmitPrompt',
    showReason: true,
    showPromotion: true,
    variant: 'warning',
    icon: 'Edit3Icon',
    action: { key: 'approvalLifecycle.rejected.cta', route: 'account-settings' },
  },
  'rejected-unknown': {
    titleKey: 'approvalLifecycle.rejected.title',
    bodyKeys: ['approvalLifecycle.rejected.body'],
    promptKey: 'approvalLifecycle.rejected.resubmitPrompt',
    showReason: true,
    showPromotion: true,
    variant: 'warning',
    icon: 'Edit3Icon',
    action: { key: 'approvalLifecycle.rejected.cta', route: 'account-settings' },
  },
  completed: {
    titleKey: 'approvalLifecycle.completed.title',
    bodyKeys: ['approvalLifecycle.completed.body'],
    variant: 'secondary',
    icon: 'ClockIcon',
  },
  // PM 14.08.2026 item 1b. `accountTitle`, not the shared `approved.title`: the
  // PM's new wording describes an approved account WITH an active subscription,
  // which is precisely this state. The three sibling approved states keep
  // `approved.title` -- a different string ("Hesap Bilgileriniz Onaylandı"),
  // because their bodies describe an account that cannot use the subscription.
  'approved-free-access': {
    titleKey: 'approvalLifecycle.approved.accountTitle',
    bodyKeys: [],
    bodyDateKey: 'freeAccess.activeBody',
    variant: 'success',
    icon: 'GiftIcon',
  },
  'approved-free-access-available': {
    titleKey: 'freeAccess.availableTitle',
    bodyKeys: ['freeAccess.availableBody'],
    variant: 'info',
    icon: 'GiftIcon',
    action: { key: 'freeAccess.claimCta', route: 'financial-settings' },
  },
  'free-access-ended': {
    titleKey: 'freeAccess.endedTitle',
    bodyKeys: ['freeAccess.endedBody'],
    variant: 'warning',
    icon: 'ClockIcon',
    action: { key: 'approvalLifecycle.approved.cta', route: 'financial-settings' },
  },
  'free-access-ending': {
    titleKey: 'freeAccess.endingTitle',
    bodyKeys: ['freeAccess.endingBody'],
    variant: 'warning',
    icon: 'ClockIcon',
    action: { key: 'approvalLifecycle.approved.cta', route: 'financial-settings' },
  },
  'approved-access': {
    titleKey: 'approvalLifecycle.approved.accountTitle',
    bodyKeys: ['approvalLifecycle.approved.activeBody'],
    variant: 'success',
    icon: 'CheckCircleIcon',
  },
  'approved-no-access': {
    titleKey: 'approvalLifecycle.approved.title',
    bodyKeys: ['approvalLifecycle.approved.noAccessBody'],
    variant: 'success',
    icon: 'CheckCircleIcon',
    action: { key: 'approvalLifecycle.approved.cta', route: 'financial-settings' },
  },
  'approved-subscription-recovery': {
    titleKey: 'approvalLifecycle.approved.title',
    bodyKeys: ['approvalLifecycle.approved.existingSubscriptionBody'],
    variant: 'success',
    icon: 'CheckCircleIcon',
    action: { key: 'approvalLifecycle.approved.subscriptionCta', route: 'financial-settings' },
  },
  // PM 04.08.2026 item 12. `bodyDateKey` interpolates the CANCELLATION
  // boundary, deliberately not `deadlineKey` -- that one carries the approval
  // review deadline, which is a different date entirely.
  // Notice-only: the user still has access until the boundary, so there is
  // nothing to act on yet and the card carries no button. The restart button
  // lives on 'subscription-expired', where it is the only way forward.
  'subscription-canceled': {
    titleKey: 'approvalLifecycle.subscriptionCanceled.title',
    bodyKeys: [],
    bodyDateKey: 'approvalLifecycle.subscriptionCanceled.body',
    variant: 'warning',
    icon: 'XCircleIcon',
  },
  // PM 05.08.2026 item 7. The sibling of 'subscription-canceled', shown in the
  // same slot once the retained window has closed. Deliberately carries no
  // date: the server stops sending offer.blockedUntil once the boundary has
  // passed (services/subscriptionOffer.js), and the PM's copy needs none.
  'subscription-expired': {
    titleKey: 'approvalLifecycle.subscriptionExpired.title',
    bodyKeys: ['approvalLifecycle.subscriptionExpired.body'],
    variant: 'warning',
    icon: 'XCircleIcon',
    action: { key: 'approvalLifecycle.subscriptionExpired.cta', route: 'financial-settings' },
  },
  // PM 14.08.2026 item 3. Home page only, once per subscription term. Body is
  // one sentence; no deadline line, no channel line, no button -- the user has
  // just been told everything works, there is nothing to act on.
  'subscription-welcome': {
    titleKey: 'approvalLifecycle.subscriptionWelcome.title',
    bodyKeys: ['approvalLifecycle.subscriptionWelcome.body'],
    variant: 'success',
    icon: 'CheckCircleIcon',
  },
  // PM 14.08.2026 item 3, owner answers R4/R7: "showed once than we shouldn't
  // show any card in there" / "make homepage goes silent permanently after the
  // welcome and approved text only on account settings". Once a subscriber has
  // been greeted, the home page is out of the lifecycle-narration business.
  // This entry exists so `presentation[state]` below still resolves; `hidden`
  // is what stops the card from rendering at all.
  'dashboard-quiet': {
    hidden: true,
    variant: 'info',
    icon: 'CheckCircleIcon',
  },
  'sticky-approved': {
    titleKey: 'approvalLifecycle.approved.title',
    bodyKeys: ['approvalLifecycle.approved.stickyBody'],
    showReason: true,
    variant: 'success',
    icon: 'CheckCircleIcon',
    action: { key: 'approvalLifecycle.pending.detailsCta', route: 'account-settings' },
  },
}

// PM 05.08.2026 item 3 applies to every surface that renders the
// `submitted.title` banner, not just the one in the PM's screenshot -- leaving
// the deleted lines on a sibling surface would put them one click away.
const accountSettingsPendingPresentation = {
  titleKey: 'approvalLifecycle.submitted.title',
  bodyKeys: ['approvalLifecycle.submitted.body'],
  variant: 'info',
  icon: 'CheckCircleIcon',
}

// PM 14.08.2026 item 1: on account settings the approval banner is about
// APPROVAL, not about the subscription. Starting a subscription moves the
// review window to `active` and turns access on, which used to swap this
// banner for the "Aramıza Hoş Geldiniz" pending card. The PM's rule is that it
// "aynen duracak" until an admin decides -- so every pending state on this
// surface renders the same submitted banner.
const ACCOUNT_SETTINGS_PENDING_STATES = [
  'pending-not-started',
  'pending-active',
  'pending-started-no-access',
  'pending-unknown',
]

// Both mean "this user has a live subscription": `pending-active` only exists
// when hasAccess is true (see the pending branch below), and `approved-access`
// by name. On the home page they get one greeting between them, then silence.
const DASHBOARD_QUIET_STATES = ['pending-active', 'approved-access']

export const resolveApprovalLifecycle = (
  account = {},
  { surface = 'default', subscriptionWelcomeSeen = false } = {},
) => {
  const approvalStatus = approvalStatusOf(account)
  const standing = standingOf(account)
  const review = reviewOf(account)
  const hasAccess = accessOf(account)
  const subscription = (account && account.subscription) || {}
  const hasCurrentSubscription = ['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(subscription.status)
    && Boolean(subscription.hasSubscription || hasAccess)
  const freeAccess = freeAccessOf(account)
  const approvedStanding = Boolean(standing && standing.reason === 'approved')
  const freeActive = approvedStanding && hasAccess && !hasCurrentSubscription && isFreeAccessActive(freeAccess)
  const freeClaimable = approvedStanding && freeAccess.readinessKnown && freeAccess.claimable && !hasCurrentSubscription
  const freeEnded = approvedStanding && subscription.accessReason === 'free-access-ended'
  const freeStartsOnApproval = !approvedStanding && freeAccess.eligible && freeAccess.state === 'none' && !hasCurrentSubscription
  const stickyApproved = approvalStatus === 'rejected'
    && standing && standing.allowed === true && standing.reason === 'approved'
  const cancellationBoundary = retainedCancellationBoundary(subscription)

  let state
  if (account.accountBlocked) state = 'blocked'
  else if (account.resubmissionJustConfirmed && approvalStatus === 'pending') state = 'resubmitted'
  else if (account.submissionJustConfirmed && approvalStatus === 'pending') state = 'submitted'
  else if (surface === 'account-settings'
    && approvalStatus === 'pending'
    && standing
    && standing.reason === 'approved') state = 'resubmitted'
  else if (freeActive && !cancellationBoundary) state = 'approved-free-access'
  else if (freeClaimable) state = 'approved-free-access-available'
  else if (freeEnded) state = 'free-access-ended'
  else if (stickyApproved) state = 'sticky-approved'
  else if (approvalStatus === 'rejected') {
    if (review.state === 'completed') state = 'rejected-completed'
    else if (review.state === 'active' && review.deadlineAt) state = 'rejected-active'
    else if (review.state === 'not-started'
      && account.promotion && account.promotion.redeemed === false) {
      state = 'rejected-not-started'
    } else state = 'rejected-unknown'
  } else if (cancellationBoundary) {
    // Item 12: outranks the pending and approved branches so a canceled user
    // is no longer greeted with "Aramıza Hoş Geldiniz", and so an approved
    // one is not told "Aboneliğiniz aktiftir" after canceling. It stays BELOW
    // `blocked`, the just-submitted confirmations, and `rejected`, all of
    // which are more urgent or more immediately actionable.
    state = 'subscription-canceled'
  } else if (subscription.status === 'CANCELED' && hasAccess === false) {
    // PM 05.08.2026 item 7: once the retained window closes, the user is not
    // "canceled with access until X" any more -- they have no access at all and
    // the only way forward is a new subscription. Sits immediately after the
    // retained branch so the two occupy the same precedence slot: below
    // `blocked`, the just-submitted confirmations and `rejected`, above pending
    // and approved. `hasAccess` is the server's own evaluation
    // (services/subscriptionAccess.js), not a date compared in the browser --
    // and the date is not even available here, because the server stops
    // sending offer.blockedUntil once the boundary has passed.
    state = 'subscription-expired'
  } else if (approvalStatus === 'pending') {
    if (standing && standing.reason === 'approved') state = 'resubmitted'
    else if (review.state === 'completed') state = 'completed'
    else if (review.state === 'active' && review.deadlineAt) {
      state = hasAccess ? 'pending-active' : 'pending-started-no-access'
    } else if (review.state === 'not-started') state = 'pending-not-started'
    else state = 'pending-unknown'
  } else if (approvalStatus === 'approved'
    || account.approved
    || (standing && standing.reason === 'approved')) {
    if (hasAccess) state = 'approved-access'
    else state = hasCurrentSubscription ? 'approved-subscription-recovery' : 'approved-no-access'
  } else state = 'not-submitted'

  let selectedPresentation = presentation[state]
  const promotionRedeemed = Boolean(presentation[state].showPromotion)
    && state.indexOf('rejected') === 0
    && Boolean(account.promotion && account.promotion.redeemed === true)

  if (surface === 'account-settings' && ACCOUNT_SETTINGS_PENDING_STATES.includes(state)) {
    selectedPresentation = accountSettingsPendingPresentation
  } else if (surface === 'dashboard' && state === 'approved-free-access') {
    state = freeAccess.daysLeft !== null && freeAccess.daysLeft <= FREE_ACCESS_REMINDER_WINDOW_DAYS
      ? 'free-access-ending' : 'dashboard-quiet'
    selectedPresentation = presentation[state]
  } else if (surface === 'dashboard' && DASHBOARD_QUIET_STATES.includes(state)) {
    // The greeting is owed to a subscription that actually started. A missing
    // `startedAt` (data gap) means no greeting and straight to quiet -- never a
    // fallback to the old `pending.title` card.
    const welcomeEligible = Boolean(subscription.startedAt) && hasAccess === true
    state = welcomeEligible && !subscriptionWelcomeSeen
      ? 'subscription-welcome'
      : 'dashboard-quiet'
    selectedPresentation = presentation[state]
  }

  if (freeStartsOnApproval && surface !== 'account-settings') {
    const hint = {
      'not-submitted': 'approvalLifecycle.notSubmitted.freeAccessHint',
      'pending-not-started': 'freeAccess.startsOnApproval',
      'rejected-not-started': 'freeAccess.startsOnApproval',
    }[state]
    if (hint) selectedPresentation = { ...selectedPresentation, bodyKeys: [...selectedPresentation.bodyKeys, hint] }
  }
  return {
    state,
    surface,
    freeAccess,
    bodyDate: freeActive || freeEnded ? freeAccess.endsAt : cancellationBoundary,
    daysLeft: freeAccess.daysLeft,
    approvalStatus,
    hasAccess,
    subscriptionStartedAt: subscription.startedAt || null,
    subscriptionAccessEndsAt: cancellationBoundary,
    review,
    reviewComment: account.approval && account.approval.reviewComment,
    emailVerified: account.emailApproved === true,
    // Computed from the state BEFORE any dashboard override, so a rejected
    // account's promotion note cannot be lost to a surface swap.
    promotionRedeemed,
    ...selectedPresentation,
  }
}

export const formatApprovalLifecycleDate = (value, locale = 'tr-TR') => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  // PM 06.08.2026 item 3. Numeric dd.MM.yyyy, not "21 Ağustos 2026": the
  // package copy the user just read says 21.08.2026, and every other date
  // surface (subscriptionCommercial.js, Pricing.vue, PurchaseCallback.vue,
  // Approval/List.vue) is already 2-digit. This was the last long-month one.
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Istanbul',
  }).format(date)
}
